import { EventEmitter, OnDestroy, Output, inject } from '@angular/core';
import { Component, OnInit, Input } from '@angular/core';
import {
  ControlContainer,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CustomerEquipmentUpgradeAgreement } from '@sales/modules/mobilephone/models/equipment-upgrade-agreement.model';
import {
  GradingQuestion,
  InsuranceEligibility,
  QuestionAnswerPair,
} from '@sales/modules/mobilephone/models/equipment-api.model';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { IconNames } from '@shared/enums/icon-names';
import { debounceTime, tap } from 'rxjs/operators';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { RadioButtonListItemComponent } from '@app/shared/components/atoms/radio-button-list/radio-button-list-item/radio-button-list-item.component';
import { RadioButtonListComponent } from '@app/shared/components/atoms/radio-button-list/radio-button-list.component';
import { MatRadioGroup } from '@angular/material/radio';
import { IceCardComponent } from '@app/shared/components/atoms/ice-card/ice-card.component';

@Component({
  selector: 'app-mobile-phone-grading',
  templateUrl: './mobile-phone-grading.component.html',
  styleUrls: ['./mobile-phone-grading.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    IceCardComponent,
    FormsModule,
    ReactiveFormsModule,
    MatRadioGroup,
    RadioButtonListComponent,
    RadioButtonListItemComponent,
    IceAlertComponent,
    IceButtonComponent,
  ],
})
export class MobilePhoneGradingComponent implements OnInit, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);
  private formBuilder = inject(FormBuilder);
  store = inject(Store);

  IconNames = IconNames;

  @Input() numberOfSteps: number;
  @Input() gradingQuestions: GradingQuestion[];
  @Input() equipmentUpgradeAgreement: CustomerEquipmentUpgradeAgreement;
  @Input() insuranceEligibility: InsuranceEligibility;
  @Input() isLoadingGradingAnswers: boolean;
  @Output() sendGradingAnswers = new EventEmitter();
  @Output() resetInsuranceEligibility = new EventEmitter();
  @Output() gradingCompleted = new EventEmitter();
  @Output() navigateToPreviousStep = new EventEmitter();

  form: FormGroup;
  subscriptions$: Subscription[] = [];

  ngOnInit() {
    if (!this.formGroupDirective.form.controls.gradingGroup) {
      this.formGroupDirective.form.addControl('gradingGroup', this.formBuilder.group({}));
    }
    this.form = this.formGroupDirective.form.controls.gradingGroup as FormGroup;
    if (this.gradingQuestions && this.gradingQuestions.length > 0) {
      this.gradingQuestions.forEach((question: GradingQuestion) => {
        if (!this.form.controls[question.questionType]) {
          this.form.addControl(question.questionType, this.formBuilder.control(null, Validators.required));
        }
      });
      this.subscriptions$.push(
        this.form.valueChanges
          .pipe(
            debounceTime(200),
            tap(() => this.resetInsuranceEligibility.emit())
          )
          .subscribe()
      );
    }
  }

  onNextStep() {
    const questionAnswerPair: QuestionAnswerPair[] = [];
    this.gradingQuestions.forEach(question => {
      questionAnswerPair.push({
        questionType: question.questionType,
        answer: this.form.get(question.questionType).value ? 'Yes' : 'No',
      });
    });
    this.sendGradingAnswers.emit(questionAnswerPair);
  }

  onNavigateToPreviousStep() {
    this.navigateToPreviousStep.emit();
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
